/**
 * Global jobs context.
 *
 * Tracks currently-active enrichment / scrape jobs so a banner stays visible
 * even when the user navigates away from the page that started the job.
 *
 * - On mount: hydrates from /api/scrape-jobs?status=running|queued so jobs
 *   started in a previous session show up immediately.
 * - Pollers tick every 3s and remove jobs when they hit done/failed.
 * - `track(jobId, label)` registers a new job from any page.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import api, { API_BASE, STORAGE_KEY } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const JobsContext = createContext(null);

const POLL_MS = 3000;
// When a bulk enrich_all is running, we speed up polling so the live banner
// (avatar + platform icons) updates smoothly as each realtor completes.
const FAST_POLL_MS = 1500;
// When SSE is healthy we drop polling to a safety-net cadence — the stream
// pushes changes in <1s so polling only needs to catch missed events.
const SSE_SAFETY_POLL_MS = 30_000;
// Consider SSE stale if we haven't received ANY frame (event OR heartbeat)
// in this long. Heartbeats fire every 15s server-side so 25s is a safe cap.
const SSE_STALE_MS = 25_000;
const FINAL_STATES = new Set(["done", "failed", "cancelled"]);

const TYPE_LABELS = {
  enrich_all: "Enriching realtors",
  enrich_realtor: "Enriching realtor",
  enrich_personal_websites: "Enriching personal sites",
  enrich_brokerages: "Enriching brokerages",
  scrape_brokerage: "Scraping brokerage",
  scrape_all: "Scraping all brokerages",
  find_brokerages: "Finding brokerages",
  realtor_ca_import: "Importing from Realtor.ca",
  openhouse_scan: "Scanning open houses",
  photo_check: "Face-checking profile photos",
};

function defaultLabel(job) {
  return TYPE_LABELS[job?.type] || job?.type || "Background job";
}

export function JobsProvider({ children }) {
  const { user } = useAuth();
  const [jobs, setJobs] = useState({}); // {id: jobDoc}
  const [sseHealthy, setSseHealthy] = useState(false);
  const finishedCbRef = useRef({}); // {id: fn}
  const seenFinishedRef = useRef(new Set()); // suppress duplicate toasts
  const autoDismissTimersRef = useRef({}); // {jobId: setTimeout handle}

  /**
   * Schedule the "finished" banner to disappear on its own so a user
   * running a bulk enrich_all doesn't come back to a wall of stacked
   * complete banners (see the user's screenshot with 6 vertical
   * banners). `enrich_realtor` sub-jobs auto-dismiss instantly because
   * they already live in Scrape History and the aggregate progress is
   * shown in the parent enrich_all banner; everything else gets an
   * 8-second grace period so the user has time to read what completed.
   */
  const scheduleAutoDismiss = useCallback((jobId, jobType) => {
    if (!jobId) return;
    if (autoDismissTimersRef.current[jobId]) return; // already scheduled
    const delay = jobType === "enrich_realtor" ? 0 : 8000;
    autoDismissTimersRef.current[jobId] = setTimeout(() => {
      delete autoDismissTimersRef.current[jobId];
      setJobs((prev) => {
        if (!prev[jobId]) return prev;
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    }, delay);
  }, []);

  // Hydrate active jobs on mount + whenever user changes
  useEffect(() => {
    if (!user) {
      setJobs({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Include paused jobs so the live banner picks them up right after
        // a page reload. Previously paused enrich_all jobs only surfaced
        // via a separate /resumable poll rendered as a yellow chip; the
        // banner now handles both live + paused states in a single row.
        const [running, queued, paused] = await Promise.all([
          api.get("/scrape-jobs", { params: { status: "running", limit: 20 } }),
          api.get("/scrape-jobs", { params: { status: "queued", limit: 20 } }),
          api.get("/scrape-jobs", { params: { status: "paused", limit: 20 } }),
        ]);
        if (cancelled) return;
        const merged = {};
        [
          ...(running.data || []),
          ...(queued.data || []),
          ...(paused.data || []),
        ].forEach((j) => {
          merged[j.id] = j;
        });
        setJobs(merged);
      } catch {
        // No-op — banner will simply remain empty until a fresh job starts.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // ------------------------- Live stream (SSE) -------------------------
  // Preferred path: /api/jobs/stream pushes updates in <1s so the banner
  // reacts the instant a realtor completes. Polling stays as a safety-net
  // fallback (see FAST_POLL_MS logic) so if the proxy strips the stream
  // (Cloudflare buffering, corporate firewall, etc.) the UI still works.
  useEffect(() => {
    if (!user) {
      setSseHealthy(false);
      return undefined;
    }
    // Browsers can't send Authorization headers on EventSource — the JWT
    // rides as a query param instead. Only leaked to the immediate proxy
    // which already sees our bearer headers on every other call.
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) {
      setSseHealthy(false);
      return undefined;
    }
    let es;
    let staleTimer;
    let cancelled = false;

    const applyJobDoc = (raw, isFinal) => {
      // Same shape-normalization as the poll path — strip pending_realtor_ids
      // so it never lands in React state (see poll effect below for context).
      const summary = raw?.summary ? { ...raw.summary } : {};
      if (Array.isArray(summary.pending_realtor_ids)) {
        summary.pending_count = summary.pending_realtor_ids.length;
        delete summary.pending_realtor_ids;
      }
      const job = { ...raw, summary };
      setJobs((prev) => {
        // Skip if we already have this exact shape (avoid needless React
        // re-renders of the 900-row realtor table below the banner).
        const existing = prev[job.id];
        if (existing && existing.status === job.status &&
            (existing.summary?.processed ?? 0) === (job.summary?.processed ?? 0) &&
            (existing.summary?.current?.realtor_id ?? "") === (job.summary?.current?.realtor_id ?? "") &&
            existing.finished_at === job.finished_at) {
          return prev;
        }
        return { ...prev, [job.id]: job };
      });
      // Handle terminal-state one-shot side-effects (toast + finish callback).
      if (isFinal || FINAL_STATES.has(job.status)) {
        if (!seenFinishedRef.current.has(job.id)) {
          seenFinishedRef.current.add(job.id);
          const cb = finishedCbRef.current[job.id];
          if (cb) {
            try { cb(job); } catch { /* ignore */ }
            delete finishedCbRef.current[job.id];
          }
          // Individual realtor enrichments (enrich_realtor) run frequently
          // during a bulk enrich_all — each one otherwise stacks up a
          // "complete" toast on screen for the default 4-8s. The user
          // sees these events in the Scrape History panel anyway, so we
          // suppress the noisy toasts here. Bulk jobs and other one-off
          // types still surface a toast so users know they're done.
          if (job.type !== "enrich_realtor") {
            if (job.status === "done") {
              toast.success(`${defaultLabel(job)} — complete`, {
                description: summarize(job),
                duration: 5000,
              });
            } else if (job.status === "failed") {
              toast.error(`${defaultLabel(job)} — failed`, {
                description: (job.errors || []).join(", ").slice(0, 200) || undefined,
                duration: 6000,
              });
            }
          }
          // Regardless of toast/no-toast, auto-dismiss the persistent
          // banner so the top of the page doesn't accumulate stacked
          // "complete" rows. enrich_realtor is instant-dismiss; others
          // linger for 8 s so a user glancing over can catch the result.
          scheduleAutoDismiss(job.id, job.type);
        }
      }
    };

    const armStaleTimer = () => {
      if (staleTimer) clearTimeout(staleTimer);
      staleTimer = setTimeout(() => {
        // No frame in SSE_STALE_MS — flip back to fast polling.
        setSseHealthy(false);
      }, SSE_STALE_MS);
    };

    const open = () => {
      if (cancelled) return;
      try {
        // encodeURIComponent to guard against odd token chars (JWT is safe
        // but future-proofing costs nothing).
        const url = `${API_BASE}/jobs/stream?token=${encodeURIComponent(token)}`;
        es = new EventSource(url);
      } catch {
        setSseHealthy(false);
        return;
      }
      es.addEventListener("open", () => {
        if (cancelled) return;
        setSseHealthy(true);
        armStaleTimer();
      });
      es.addEventListener("job", (evt) => {
        if (cancelled) return;
        armStaleTimer();
        setSseHealthy(true);
        try {
          const raw = JSON.parse(evt.data);
          applyJobDoc(raw, false);
        } catch { /* malformed frame — ignore */ }
      });
      es.addEventListener("job_final", (evt) => {
        if (cancelled) return;
        armStaleTimer();
        setSseHealthy(true);
        try {
          const raw = JSON.parse(evt.data);
          applyJobDoc(raw, true);
        } catch { /* ignore */ }
      });
      es.addEventListener("idle_close", () => {
        // Server closed us because nothing was active for a while. That's
        // fine — the browser will auto-reconnect (retry hint = 3000ms).
        // Meanwhile polling handles any new jobs.
        setSseHealthy(false);
      });
      es.addEventListener("error", () => {
        if (cancelled) return;
        // EventSource auto-reconnects; we just flag the stream unhealthy
        // so the poller speeds back up until the next successful frame.
        setSseHealthy(false);
      });
    };

    open();
    return () => {
      cancelled = true;
      if (staleTimer) clearTimeout(staleTimer);
      if (es) es.close();
      setSseHealthy(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- SSE subscription is keyed on the user only; scheduleAutoDismiss is a stable ref
  }, [user]);
  // ---------------------------------------------------------------------

  // Poll cadence — when SSE is unhealthy this is the primary mechanism.
  // Speed up polling when a bulk enrich_all is ACTIVELY running so the
  // live banner (avatar + platform icons popping in) feels responsive.
  // Paused enrich_all jobs poll at the normal 3s cadence — they don't
  // change until the user clicks Resume, at which point the next tick
  // picks up the transition. Idle apps also stay at 3s.
  //
  // When SSE is HEALTHY we drop polling to a 30-second safety-net cadence
  // because the stream is pushing changes in <1s. Polling still exists as
  // a fallback in case the stream silently dies mid-run.
  const pollInterval = useMemo(() => {
    if (sseHealthy) return SSE_SAFETY_POLL_MS;
    for (const j of Object.values(jobs)) {
      if (j?.type === "enrich_all" && j.status === "running") {
        return FAST_POLL_MS;
      }
    }
    return POLL_MS;
  }, [jobs, sseHealthy]);

  // Poll all tracked jobs together
  useEffect(() => {
    const ids = Object.keys(jobs).filter((id) => !FINAL_STATES.has(jobs[id]?.status));
    if (ids.length === 0) return undefined;
    let cancelled = false;
    const tick = async () => {
      // Skip polls while the tab is hidden — browsers already throttle
      // setInterval to ~1/sec in background tabs, but this also stops us
      // from doing needless state churn when nobody's looking.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      const results = await Promise.allSettled(
        ids.map((id) => api.get(`/scrape-jobs/${id}`)),
      );
      if (cancelled) return;
      setJobs((prev) => {
        const next = { ...prev };
        results.forEach((res, idx) => {
          const id = ids[idx];
          if (res.status !== "fulfilled") return;
          const raw = res.value.data;
          // Strip the huge `summary.pending_realtor_ids` array (up to ~900
          // UUIDs, ~35 KB) before it lands in React state. Nothing renders
          // it — the banner just needs the counts. Keeping it in state
          // would balloon every consumer's snapshot and make garbage
          // collection thrash during long enrich_all runs.
          const summary = raw?.summary ? { ...raw.summary } : {};
          if (Array.isArray(summary.pending_realtor_ids)) {
            summary.pending_count = summary.pending_realtor_ids.length;
            delete summary.pending_realtor_ids;
          }
          const job = { ...raw, summary };
          next[id] = job;
          if (FINAL_STATES.has(job.status) && !seenFinishedRef.current.has(id)) {
            seenFinishedRef.current.add(id);
            const cb = finishedCbRef.current[id];
            if (cb) {
              try {
                cb(job);
              } catch {
                /* ignore */
              }
              delete finishedCbRef.current[id];
            }
            if (job.type !== "enrich_realtor") {
              if (job.status === "done") {
                toast.success(`${defaultLabel(job)} — complete`, {
                  description: summarize(job),
                  duration: 5000,
                });
              } else if (job.status === "failed") {
                toast.error(`${defaultLabel(job)} — failed`, {
                  description: (job.errors || []).join(", ").slice(0, 200) || undefined,
                  duration: 6000,
                });
              }
            }
            // Auto-hide the persistent banner (see scheduleAutoDismiss).
            scheduleAutoDismiss(job.id, job.type);
          }
        });
        return next;
      });
    };
    const handle = setInterval(tick, pollInterval);
    // Kick off an immediate tick so users see status sooner
    tick();
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [jobs, pollInterval, scheduleAutoDismiss]);

  const track = useCallback((jobId, opts = {}) => {
    if (!jobId) return;
    if (opts.onFinish) finishedCbRef.current[jobId] = opts.onFinish;
    setJobs((prev) => {
      if (prev[jobId]) return prev;
      return {
        ...prev,
        [jobId]: {
          id: jobId,
          type: opts.type || "",
          status: "running",
          target_label: opts.label || "",
          started_at: new Date().toISOString(),
        },
      };
    });
  }, []);

  const dismiss = useCallback((jobId) => {
    setJobs((prev) => {
      if (!prev[jobId]) return prev;
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
  }, []);

  const activeJobs = useMemo(
    () => Object.values(jobs).filter((j) => !FINAL_STATES.has(j.status)),
    [jobs],
  );
  // Jobs that finished but haven't been dismissed yet — the global banner
  // keeps them visible so an admin who walks away for a coffee can still
  // see WHAT completed rather than missing a 5-second toast. They now
  // auto-dismiss after 8 s via scheduleAutoDismiss (see above); this
  // memo also filters out `enrich_realtor` sub-jobs as a safety net so
  // even if a timer fires late they don't stack up.
  const recentlyFinishedJobs = useMemo(() => {
    const cutoffMs = Date.now() - 8 * 1000;
    return Object.values(jobs)
      .filter((j) => FINAL_STATES.has(j.status))
      .filter((j) => j.type !== "enrich_realtor")
      .filter((j) => {
        const t = j.finished_at ? Date.parse(j.finished_at) : Date.now();
        return t >= cutoffMs;
      });
  }, [jobs]);
  // Keep finishedJobs export for backward compatibility with any consumer
  // that only cares about "which have completed", regardless of freshness.
  const finishedJobs = useMemo(
    () => Object.values(jobs).filter((j) => FINAL_STATES.has(j.status)),
    [jobs],
  );

  const value = useMemo(
    () => ({
      jobs,
      activeJobs,
      finishedJobs,
      recentlyFinishedJobs,
      track,
      dismiss,
    }),
    [jobs, activeJobs, finishedJobs, recentlyFinishedJobs, track, dismiss],
  );

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
}

export function useJobs() {
  const ctx = useContext(JobsContext);
  if (!ctx) throw new Error("useJobs must be used inside <JobsProvider>");
  return ctx;
}

function summarize(job) {
  const s = job.summary || {};
  if (job.type === "enrich_all") {
    return `${s.successful ?? 0} ok · ${s.failed ?? 0} failed`;
  }
  if (job.type === "enrich_realtor") {
    return `Filled ${s.fields_filled ?? 0} fields`;
  }
  if (job.type === "enrich_personal_websites") {
    return `${s.succeeded ?? 0} ok · ${s.fields_filled ?? 0} fields filled`;
  }
  if (job.type === "enrich_brokerages") {
    return `${s.succeeded ?? 0} ok · ${s.phones_filled ?? 0} phones · ${s.addresses_filled ?? 0} addresses · ${s.parents_identified ?? 0} parents`;
  }
  if (job.type === "scrape_brokerage") {
    return `${s.realtors_found ?? 0} realtors · ${s.new_added ?? 0} new`;
  }
  if (job.type === "scrape_all") {
    return `${s.successful ?? 0} ok · ${s.failed ?? 0} failed`;
  }
  return undefined;
}

export { TYPE_LABELS, defaultLabel };
